<script setup>
import { computed, ref, watch } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Checkbox from 'primevue/checkbox'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  brand: {
    type: Object,
    default: null
  },
  mode: {
    type: String,
    default: 'create'
  },
  submitting: {
    type: Boolean,
    default: false
  },
  fieldErrors: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['close', 'submit', 'clear-field-error'])

const name = ref('')
const isActive = ref(true)
const localErrors = ref({})

const dialogTitle = computed(() =>
  props.mode === 'edit' ? 'Редактирование бренда' : 'Создание бренда'
)

const submitLabel = computed(() =>
  props.mode === 'edit' ? 'Сохранить' : 'Создать'
)

const submitIcon = computed(() =>
  props.mode === 'edit' ? 'pi pi-check' : 'pi pi-plus'
)

watch(
  () => props.visible,
  (newValue) => {
    if (newValue) {
      name.value = props.brand?.name ?? ''
      isActive.value = props.brand?.isActive ?? true
      localErrors.value = {}
    } else {
      resetForm()
    }
  }
)

watch(
  () => props.brand,
  (newBrand) => {
    if (props.visible) {
      name.value = newBrand?.name ?? ''
      isActive.value = newBrand?.isActive ?? true
    }
  }
)

function resetForm() {
  name.value = ''
  isActive.value = true
  localErrors.value = {}
}

function getFieldError(field) {
  return (
    localErrors.value[field] ||
    props.fieldErrors?.[field] ||
    props.fieldErrors?.[field.charAt(0).toUpperCase() + field.slice(1)] ||
    ''
  )
}

function hasFieldError(field) {
  return Boolean(getFieldError(field))
}

function handleNameInput() {
  localErrors.value.name = ''
  emit('clear-field-error', 'name')
}

function validateForm() {
  localErrors.value = {}

  if (!name.value.trim()) {
    localErrors.value.name = 'Введите название бренда'
  }

  return Object.keys(localErrors.value).length === 0
}

function submitForm() {
  if (!validateForm()) return

  emit('submit', {
    name: name.value.trim(),
    isActive: isActive.value
  })
}
</script>

<template>
  <Dialog
    :visible="props.visible"
    modal
    :header="dialogTitle"
    :style="{ width: 'min(500px, 95vw)' }"
    @update:visible="emit('close')"
  >
    <div class="form-container">
      <div class="field">
        <label for="brand-name">Название бренда</label>
        <InputText
          id="brand-name"
          v-model="name"
          placeholder="Введите название бренда"
          class="w-full"
          :invalid="hasFieldError('name')"
          @input="handleNameInput"
        />
        <small v-if="hasFieldError('name')" class="field-error">
          {{ getFieldError('name') }}
        </small>
      </div>

      <div class="checkbox-field">
        <Checkbox
          v-model="isActive"
          inputId="brand-active"
          binary
        />
        <label for="brand-active">Активный бренд</label>
      </div>
    </div>

    <template #footer>
      <Button
        label="Отмена"
        severity="secondary"
        outlined
        :disabled="props.submitting"
        @click="emit('close')"
      />
      <Button
        :label="submitLabel"
        :icon="submitIcon"
        :loading="props.submitting"
        :disabled="props.submitting"
        @click="submitForm"
      />
    </template>
  </Dialog>
</template>

<style scoped>
.form-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox-field {
  display: flex;
  align-items: center;
  gap: 10px;
}
</style>
